/**
 * Copy, timing, and storage keys for the Web → App invite (PB-303).
 *
 * One component, two trigger kinds ("welcome" and "gated"), each with copy
 * variants. Keeping the copy in one table makes the four mockup variants easy to
 * audit against the card.
 */

/**
 * How long a dismissed *welcome* prompt stays suppressed, per user.
 *
 * The card is internally inconsistent (requirements + test steps say 3 days; the
 * acceptance-criteria line says 7). We ship 3 (the majority) as a single knob so
 * the value can move without touching logic. The gated variant ignores this.
 */
export const WELCOME_COOLDOWN_DAYS = 3

export const WELCOME_COOLDOWN_MS = WELCOME_COOLDOWN_DAYS * 24 * 60 * 60 * 1000

/**
 * How long the welcome prompt waits after the post-auth navigation before it
 * appears. Firing it the instant login lands is jarring (V1 sprint test #11) —
 * the user hasn't seen the page yet. A short delay lets them land, then the
 * non-blocking bottom bar slides up. Consumed by `usePendingWelcome`.
 */
export const WELCOME_DELAY_MS = 30_000

/**
 * Cookie holding per-user welcome-dismissal timestamps: `{ [userId]: epochMs }`.
 * Non-httpOnly so the client can gate without a round-trip; SSR can read it too.
 *
 * Name follows the app's cookie convention (`hh_`-prefixed snake_case, cf.
 * `AuthCookie` in `@/lib/config`) — a colon isn't a valid RFC 6265 cookie-name
 * token, so the `hubhive:`-style keys used for localStorage are avoided here.
 */
export const DISMISSAL_COOKIE = 'hh_app_invite'

/** sessionStorage flag carrying a welcome trigger across the post-auth navigation. */
export const PENDING_WELCOME_KEY = 'hubhive:app-invite:pending-welcome'

/**
 * QR / download target. Points at the marketing site root, which carries the
 * download CTA and can route by OS. Isolated here so it can later be swapped for
 * a smart link (OneLink/Branch) without hunting through components. Store links
 * themselves live in the canonical `shared/not-found/store-links.ts`.
 */
export const APP_DOWNLOAD_URL = 'https://hubhive.com'

/** What triggered a welcome prompt — for analytics / future copy tuning. */
export type WelcomeSource = 'sign-in' | 'sign-up' | 'join-hive'

/** Web-unsupported interactions that raise the gated prompt. */
export type GatedAction = 'list-product'

interface InviteCopy {
  title: string
  /** Shown with the badge CTA (mobile web). */
  body: string
  /** Shown with the QR CTA (desktop web) — mentions scanning. */
  bodyScan: string
}

export const WELCOME_COPY: InviteCopy = {
  title: 'Get the full HubHive experience',
  body: 'Stay connected, get notifications, and unlock everything HubHive has to offer in the app.',
  bodyScan: 'Scan to download the app to get a better experience and interact with more features.',
}

const GATED_GENERIC: InviteCopy = {
  title: 'Get the App',
  body: 'Download the app to get a better experience and interact with more features.',
  bodyScan: 'Scan to download the app to get a better experience and interact with more features.',
}

const GATED_COPY: Record<GatedAction, InviteCopy> = {
  'list-product': {
    title: 'Listing products is in the app',
    body: "Add products, photos and pricing right from your phone — it's the easiest way to start selling on HubHive.",
    bodyScan: 'Scan to get the app and add products, photos and pricing right from your phone.',
  },
}

export function gatedCopy(action: GatedAction | null): InviteCopy {
  if (action && action in GATED_COPY) return GATED_COPY[action]
  return GATED_GENERIC
}
